import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { talentyApi, type DomainContentItem, type JobLandingPage } from '../services/talentyApi';
import './FamiliesOverviewPage.css';

const FamiliesOverviewPage: React.FC = () => {
  const [families, setFamilies] = useState<DomainContentItem[]>([]);
  const [allJobs, setAllJobs] = useState<JobLandingPage[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        // Fetch both families and jobs to get complete hierarchical information
        const [familiesResponse, jobsResponse] = await Promise.all([
          talentyApi.getFamilyContentItemsOverview(),
          talentyApi.getAllJobLandingPages()
        ]);
        
        // Debug: Log the API responses
        console.log('=== FAMILIES API DEBUG INFO ===');
        console.log('Families Response:', familiesResponse);
        console.log('Jobs Response:', jobsResponse);
        console.log('Individual Family Items:');
        familiesResponse.forEach((family, index) => {
          console.log(`Family ${index}:`, {
            id: family.id,
            slug: family.slug,
            title: family.title,
            domainSlugs: family.domainSlugs,
            jobSlugs: family.jobSlugs,
            domain: family.domain,
            family: family.family
          });
        });
        console.log('Individual Job Items:');
        jobsResponse.pages.slice(0, 5).forEach((job, index) => {
          console.log(`Job ${index}:`, {
            slug: job.slug,
            title: job.title,
            domainSlug: job.domainSlug,
            familySlug: job.familySlug,
            domain: job.domain,
            family: job.family
          });
        });
        console.log('=== END FAMILIES DEBUG INFO ===');
        
        setFamilies(familiesResponse);
        setAllJobs(jobsResponse.pages);
      } catch (err) {
        console.error('Error fetching families data:', err);
        setError('Die Jobfamilien konnten nicht geladen werden.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Helper function to get jobs for a family
  const getJobsForFamily = (family: DomainContentItem) => {
    return allJobs.filter(job => job.familySlug === family.slug);
  };

  // Helper function to get job slugs for a family
  const getJobSlugsForFamily = (family: DomainContentItem) => {
    const familyJobs = getJobsForFamily(family);
    return familyJobs.map(job => job.slug);
  };

  // Helper function to get job count for a family
  const getJobCountForFamily = (family: DomainContentItem) => {
    const familyJobs = getJobsForFamily(family);
    return familyJobs.length;
  };

  // Helper function to get unique domains for a family
  const getDomainsForFamily = (family: DomainContentItem) => {
    const familyJobs = getJobsForFamily(family);
    const uniqueDomains = Array.from(new Set(familyJobs.map(job => job.domain).filter((domain): domain is string => Boolean(domain))));
    return uniqueDomains;
  };

  if (loading) {
    return (
      <div className="family-list-page">
        <div className="container">
          <div className="loading">Lade Jobfamilien...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="family-list-page">
        <div className="container">
          <div className="error">
            <h2>Fehler beim Laden der Jobfamilien</h2>
            <p>{error}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="family-list-page">
      <div className="container">
        <div className="page-header">
          <h1>Jobfamilien bei Digital Workforce Group</h1>
          <p>Entdecke unsere verschiedenen Jobfamilien</p>
        </div>

        <div className="families-grid">
          {families.map(family => {
            const jobSlugs = getJobSlugsForFamily(family);
            const jobCount = getJobCountForFamily(family);
            const domains = getDomainsForFamily(family);
            
            return (
              <div key={family.id} className="family-card">
                <div className="family-header">
                  <h3 className="family-title">{family.title}</h3>
                  <div className="family-stats">
                    {domains.length > 0 && (
                      <span className="stat-badge">
                        {domains.length} {domains.length === 1 ? 'Bereich' : 'Bereiche'}
                      </span>
                    )}
                    {jobCount > 0 && (
                      <span className="stat-badge">
                        {jobCount} Job{jobCount !== 1 ? 's' : ''}
                      </span>
                    )}
                  </div>
                </div>
                
                {domains.length > 0 && (
                  <div className="family-domains">
                    <h4>Bereiche:</h4>
                    <div className="domains-list">
                      {domains.map((domain, index) => (
                        <span key={index} className="domain-tag">
                          {domain}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Hidden by default, shown on hover */}
                {jobSlugs.length > 0 && (
                  <div className="family-jobs-hover">
                    <h4>Job Slugs:</h4>
                    <div className="jobs-slugs-list">
                      {jobSlugs.slice(0, 8).map((jobSlug: string, index: number) => (
                        <Link
                          key={index}
                          to={`/job/${jobSlug}`}
                          className="job-slug-link"
                        >
                          {jobSlug}
                        </Link>
                      ))}
                      {jobSlugs.length > 8 && (
                        <span className="more-jobs">
                          und {jobSlugs.length - 8} weitere Jobs...
                        </span>
                      )}
                    </div>
                  </div>
                )}
                
                <div className="family-actions">
                  <Link to={`/family/${family.slug}`} className="btn btn-primary">
                    Jobfamilie ansehen
                  </Link>
                  {jobCount > 0 && (
                    <Link to={`/jobs?family=${family.slug}`} className="btn btn-secondary">
                      Alle Jobs anzeigen
                    </Link>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {families.length === 0 && (
          <div className="no-families">
            <h3>Keine Jobfamilien gefunden</h3>
            <p>Komm später wieder.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default FamiliesOverviewPage;
